import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import {
  Alert,
  Container,
  Grid,
  Loading,
  Stack,
} from "../../design-system";
import NotesTimeline from "./NotesTimeline";
import PerformanceTrends from "./PerformanceTrends";
import PlayerComparisonChart from "./PlayerComparisonChart";
import ScoutingSummaryCards from "./ScoutingSummaryCards";
import ScoutingEmptyState from "./ScoutingEmptyState";

const NUMERIC_SOURCES = [
  "stats",
  "statTotals",
  "estadisticas",
  "estadisticasTotales",
  "totales",
  "totals",
  "summary",
  "resumen",
  "scoutingStats",
  "playerStats",
  "metrics",
];

const TREND_SOURCES = [
  "statsByTemporada",
  "trend",
  "trends",
  "history",
  "historial",
  "timeline",
  "performanceHistory",
  "performanceTrend",
  "serieTemporal",
  "series",
  "statsByJornada",
  "estadisticasPorJornada",
  "monthlyStats",
  "monthStats",
  "jornadas",
];

const GOAL_KEYS = ["goles", "goals", "golesTotal", "totalGoals", "golesMarcados"];
const STARTER_KEYS = ["titularidades", "starters", "titularidadesTotales", "totalStarters"];
const MINUTE_KEYS = ["minutos", "minutes", "minutosJugados", "totalMinutes", "mins"];
const MATCH_KEYS = ["partidosJugados", "matches", "partidos", "pj", "gamesPlayed"];
const CARD_KEYS = ["tarjetas", "cards", "totalCards"];
const YELLOW_KEYS = ["tarjetasAmarillas", "yellowCards", "amarillas"];
const RED_KEYS = ["tarjetasRojas", "redCards", "rojas"];
const SECOND_YELLOW_KEYS = ["dobleAmarilla", "secondYellowCards", "segundasAmarillas"];

const TREND_LABEL_CANDIDATES = ["label", "name", "periodo", "period", "tramo"];

const toNumber = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const normalized = Number(value.replace(",", "."));
    return Number.isFinite(normalized) ? normalized : null;
  }

  return null;
};

const getCandidateSources = (entity = {}) => {
  const nested = NUMERIC_SOURCES.map((key) => entity?.[key]).filter(Boolean);
  return [entity, ...nested];
};

const findFirstNumber = (entity, keys) => {
  const sources = getCandidateSources(entity);
  for (const source of sources) {
    for (const key of keys) {
      const value = toNumber(source?.[key]);
      if (value != null) {
        return value;
      }
    }
  }
  return 0;
};

const findLabel = (point) => {
  for (const key of TREND_LABEL_CANDIDATES) {
    if (point?.[key]) {
      return String(point[key]);
    }
  }

  if (point?.jornada != null) {
    return `J${point.jornada}`;
  }

  const monthValue = point?.mes || point?.month;
  if (monthValue) {
    return String(monthValue);
  }

  const rawDate = point?.fecha || point?.date;
  if (rawDate) {
    const parsed = new Date(rawDate);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleDateString("es-ES", { day: "numeric", month: "short" });
    }
  }

  return null;
};

const findSortValue = (point, index) => {
  if (typeof point?.jornada === "number") {
    return point.jornada;
  }

  const monthValue = toNumber(point?.monthIndex ?? point?.mesIndex ?? point?.mesNumero);
  if (monthValue != null) {
    return monthValue;
  }

  const rawDate = point?.fecha || point?.date;
  if (rawDate) {
    const parsed = new Date(rawDate);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.getTime();
    }
  }

  return index + 1;
};

const extractTrendSeries = (player = {}) => {
  const trendCollections = [
    ...TREND_SOURCES.map((key) => player?.[key]),
    ...NUMERIC_SOURCES.flatMap((key) => TREND_SOURCES.map((trendKey) => player?.[key]?.[trendKey])),
  ].filter(Array.isArray);

  const firstCollection = trendCollections.find((collection) => collection.length > 0) || [];

  return firstCollection
    .map((point, index) => {
      const label = findLabel(point);
      if (!label) {
        return null;
      }

      return {
        label,
        sortValue: findSortValue(point, index),
        goals: findFirstNumber(point, GOAL_KEYS),
        matches: findFirstNumber(point, MATCH_KEYS),
        minutes: findFirstNumber(point, MINUTE_KEYS),
        starters: findFirstNumber(point, STARTER_KEYS),
      };
    })
    .filter(Boolean)
    .sort((left, right) => left.sortValue - right.sortValue);
};

const normalizePlayer = (player = {}) => {
  const yellowCards = findFirstNumber(player, YELLOW_KEYS);
  const redCards = findFirstNumber(player, RED_KEYS);
  const secondYellowCards = findFirstNumber(player, SECOND_YELLOW_KEYS);
  const cardsTotal = findFirstNumber(player, CARD_KEYS) || yellowCards + redCards + secondYellowCards;

  return {
    id: String(player.jugadorId || player.id || player.codJugador || player.codigo || Math.random()),
    jugadorId: player.jugadorId || player.id || player.codJugador || null,
    name: player.jugadorNombre || player.nombre || player.name || "Jugador sin nombre",
    team: player.equipoNombre || player.clubNombre || player.teamName || player.equipo || "Sin equipo",
    stats: {
      goals: findFirstNumber(player, GOAL_KEYS),
      starters: findFirstNumber(player, STARTER_KEYS),
      cards: cardsTotal,
      minutes: findFirstNumber(player, MINUTE_KEYS),
      matches: findFirstNumber(player, MATCH_KEYS),
    },
    trendSeries: extractTrendSeries(player),
    raw: player,
  };
};

const buildMatchDate = (match = {}) => match.fechaISO || match.fecha || match.date || null;

const buildNextMatch = (matches = []) => {
  const next = matches
    .map((match) => ({
      original: match,
      parsedDate: buildMatchDate(match) ? new Date(buildMatchDate(match)) : null,
    }))
    .filter(({ parsedDate }) => parsedDate && !Number.isNaN(parsedDate.getTime()))
    .sort((left, right) => left.parsedDate.getTime() - right.parsedDate.getTime())[0];

  if (!next) {
    return null;
  }

  return {
    label: `${next.original.equipoLocal || "Local"} vs ${next.original.equipoVisitante || "Visitante"}`,
    competition: next.original.competicion || next.original.categoria || "Partido seguimiento",
    date: next.parsedDate,
  };
};

const ScoutingDashboard = ({
  followedPlayers = [],
  watchedMatches = [],
  matchNotes = [],
  agendaMatches = [],
  upcomingMatches = [],
  loading = false,
  error = null,
  onOpenNote,
}) => {
  const { t } = useTranslation('scouting');
  const normalizedPlayers = useMemo(
    () => followedPlayers.map((player) => normalizePlayer(player)),
    [followedPlayers]
  );

  const playerLookup = useMemo(
    () =>
      normalizedPlayers.reduce((accumulator, player) => {
        accumulator[String(player.jugadorId || player.id)] = player;
        return accumulator;
      }, {}),
    [normalizedPlayers]
  );

  const topPlayer = useMemo(
    () => [...normalizedPlayers].sort((left, right) => right.stats.matches - left.stats.matches)[0] || null,
    [normalizedPlayers]
  );

  const nextMatch = useMemo(
    () => buildNextMatch([...(agendaMatches || []), ...(upcomingMatches || [])]),
    [agendaMatches, upcomingMatches]
  );

  const handleOpenTimelineNote = useCallback(
    (note) => {
      if (!onOpenNote) {
        return;
      }

      const player = playerLookup[String(note.jugadorId)] || {
        jugadorId: note.jugadorId,
        name: note.jugadorNombre,
        team: note.equipoNombre,
      };

      onOpenNote(
        {
          codPartido: note.partidoId,
          codacta: note.partidoId,
          equipoLocal: note.equipoLocal,
          equipoVisitante: note.equipoVisitante,
          fechaISO: note.fecha,
          fecha: note.fecha,
          competicion: note.competicion,
          codEquipoLocal: "",
          codEquipoVisitante: "",
        },
        {
          jugadorId: player.jugadorId || note.jugadorId,
          jugadorNombre: player.name || note.jugadorNombre,
        }
      );
    },
    [onOpenNote, playerLookup]
  );

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box
          sx={{
            minHeight: 320,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Loading text={t('dashboard.loading')} />
        </Box>
      </Container>
    );
  }

  return (
    <Container className="sc-dashboard" fluid disableGutters>
      <Stack spacing={3}>
        {error && (
          <Alert severity="error" title={t('dashboard.error')}>
            {error}
          </Alert>
        )}

        {normalizedPlayers.length === 0 ? (
          !error && <ScoutingEmptyState />
        ) : (
          <>
            <ScoutingSummaryCards
              totalPlayers={normalizedPlayers.length}
              totalWatchedMatches={watchedMatches.length}
              totalNotes={matchNotes.length}
              topPlayer={topPlayer}
              nextMatch={nextMatch}
              players={normalizedPlayers}
            />

            <Grid container spacing={2}>
              <Grid item xs={12} lg={5}>
                <PlayerComparisonChart players={normalizedPlayers} />
              </Grid>
              <Grid item xs={12} lg={7}>
                <PerformanceTrends players={normalizedPlayers} />
              </Grid>
            </Grid>

            <NotesTimeline notes={matchNotes} onOpenNote={handleOpenTimelineNote} />
          </>
        )}
      </Stack>
    </Container>
  );
};

export default ScoutingDashboard;
